import { h } from 'preact';
import { SelectEntry, TextFieldEntry, TextAreaEntry } from '@epx/flow';

const getTriggerTypes = (i18n) => [
  { value: '', label: i18n['opt.none'] || '-- None --' },
  { value: 'Timer', label: i18n['opt.Timer'] || 'Timer' },
  { value: 'Message', label: i18n['opt.Message'] || 'Message' },
  { value: 'Condition', label: i18n['opt.Condition'] || 'Condition' },
  { value: 'Signal', label: i18n['opt.Signal'] || 'Signal' },
];

const EVENT_TYPES = [
  'epx:StartEvent',
  'epx:IntermediateCatchEvent',
  'epx:IntermediateThrowEvent',
  'epx:EndEvent',
];

// Fields visible per triggerType
const VISIBILITY = {
  Timer: ['timerExpression'],
  Message: ['messageRef'],
  Condition: ['conditionExpression'],
  Signal: ['signalRef'],
};

export class SfdTriggerExtension {
  constructor(lf, i18n) {
    this._lf = lf;
    this._i18n = i18n;
  }

  isApplicable(elementType) {
    return EVENT_TYPES.includes(elementType);
  }

  getGroups(element) {
    const lf = this._lf;
    const i18n = this._i18n;
    const props = element.properties || {};
    const id = element.id;

    const update = (key, value) => {
      lf.setProperties(id, { [key]: value });
      lf.emit('node:properties-change', { id, properties: { [key]: value } });
    };

    const triggerType = props.triggerType || '';
    const visibleFields = VISIBILITY[triggerType] || [];
    const isVisible = (field) => visibleFields.includes(field);

    const entries = [
      {
        id: 'triggerType',
        component: () => (
          <SelectEntry
            id="sfd-triggerType"
            label={i18n.triggerType || 'Trigger Type'}
            value={triggerType}
            options={getTriggerTypes(i18n)}
            onChange={(val) => update('triggerType', val)}
          />
        ),
      },
    ];

    if (isVisible('timerExpression')) {
      entries.push({
        id: 'timerExpression',
        component: () => (
          <TextFieldEntry
            id="sfd-timerExpression"
            label={i18n.timerExpression || 'Timer Expression'}
            value={props.timerExpression || ''}
            placeholder="e.g. PT15M, R3/PT10S"
            description={i18n.timerExpressionHelp || 'ISO 8601 duration or cron expression'}
            onChange={(val) => update('timerExpression', val)}
            debounce={true}
          />
        ),
      });
    }

    if (isVisible('conditionExpression')) {
      entries.push({
        id: 'conditionExpression',
        component: () => (
          <TextAreaEntry
            id="sfd-conditionExpression"
            label={i18n.conditionExpression || 'Condition Expression'}
            value={props.conditionExpression || ''}
            placeholder="e.g. ${amount > 1000}"
            description={i18n.conditionExpressionHelp || 'Boolean expression evaluated at runtime'}
            onChange={(val) => update('conditionExpression', val)}
            rows={3}
            debounce={true}
          />
        ),
      });
    }

    if (isVisible('messageRef')) {
      entries.push({
        id: 'messageRef',
        component: () => (
          <TextFieldEntry
            id="sfd-messageRef"
            label={i18n.messageRef || 'Message Reference'}
            value={props.messageRef || ''}
            placeholder="Message name"
            onChange={(val) => update('messageRef', val)}
            debounce={true}
          />
        ),
      });
    }

    if (isVisible('signalRef')) {
      entries.push({
        id: 'signalRef',
        component: () => (
          <TextFieldEntry
            id="sfd-signalRef"
            label={i18n.signalRef || 'Signal Reference'}
            value={props.signalRef || ''}
            placeholder="Signal name"
            onChange={(val) => update('signalRef', val)}
            debounce={true}
          />
        ),
      });
    }

    return [
      {
        id: 'sfdTrigger',
        label: i18n.trigger || 'Trigger',
        entries,
        isEdited: () => !!triggerType,
      },
    ];
  }
}
